import { DataTable, FormSectionHeader, Heading, Loader, Timeline } from "@/components";
import { Badge, Button, Input } from "@/components/ui";
import { cn } from "@/lib/utils";
import { ArrowDownToLine, FileText, Search } from "lucide-react";
import { rowFilterOptions, uploadStatusMeta } from "../consts";
import RowDetailDialog from "./RowDetailDialog";
import useUploadDetail from "./useUploadDetail";

const UploadDetail = () => {
  const {
    upload, isCommitted, isUploadLoading, breadcrumbData, summaryCards, timelineItems, columns, rowsData,
    isRowsLoading, globalFilter, setGlobalFilter, rowFilter, onRowFilterChange, pageNumber, pageSize,
    setPageNumber, setPageSize, sorting, setSorting, selectedRow, setSelectedRow, onOpenUser, downloading,
    onDownloadFile, onDownloadErrorLog, onDownloadAudit,
  } = useUploadDetail();

  if (isUploadLoading) return <Loader />;

  if (!upload) {
    return (
      <div className="p-4 md:p-6">
        <Heading title="Upload not found" breadcrumbData={breadcrumbData} />
        <p className="text-[14px] text-gray-500 mt-4">This upload does not exist in the selected community.</p>
      </div>
    );
  }

  const statusMeta = uploadStatusMeta[upload.status];

  return (
    <div className="p-4 md:p-6">
      <Heading title="Bulk Upload Details" breadcrumbData={breadcrumbData} />

      {/* File + downloads */}
      <div className="bg-white p-4 rounded-md my-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex flex-row items-center gap-2">
          <div className="bg-green-50 rounded-md p-2">
            <FileText className="stroke-green-600" />
          </div>
          <div className="flex flex-col">
            <span className="text-[14px] font-medium flex flex-row items-center gap-2">
              {upload.fileName}
              <Badge className={cn("rounded-full", statusMeta?.className)}>{statusMeta?.label ?? upload.status}</Badge>
            </span>
            <span className="text-[12px] text-gray-400">Upload #{upload.attachmentId}</span>
          </div>
        </div>
        <div className="flex flex-row flex-wrap items-center gap-1">
          <Button variant="ghost" className="text-gray-500" disabled={!!downloading} onClick={onDownloadFile}>
            {downloading === "file" ? <Loader variant="secondary" /> : <ArrowDownToLine />} Original File
          </Button>
          <Button variant="ghost" className="text-gray-500" disabled={!!downloading} onClick={onDownloadErrorLog}>
            {downloading === "errors" ? <Loader variant="secondary" /> : <ArrowDownToLine />} Error Log
          </Button>
          {isCommitted ?
            <Button variant="ghost" className="text-gray-500" disabled={!!downloading} onClick={onDownloadAudit}>
              {downloading === "audit" ? <Loader variant="secondary" /> : <ArrowDownToLine />} Audit (CSV)
            </Button> : <></>}
        </div>
      </div>

      {/* Journey */}
      <div className="bg-white p-4 rounded-md mb-5">
        <FormSectionHeader>Journey</FormSectionHeader>
        <Timeline items={timelineItems} />
      </div>

      {isCommitted ?
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-[20px]">
            {summaryCards.map((card) => (
              <div key={card.title} className={cn(card.className, "rounded-md pb-2")}>
                <div className="bg-white flex flex-col rounded-md p-4">
                  <span className={cn("text-[24px] font-medium", card.textClassName)}>{card.count}</span>
                  <span className="text-[12px]">{card.title}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="my-5">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-3">
              <div className="flex flex-row flex-wrap items-center gap-3">
                <FormSectionHeader wrapperClassName="mb-0">Row-by-row Results</FormSectionHeader>
                {rowFilterOptions.map((option) => (
                  <Badge
                    key={option.value}
                    className={cn(
                      "rounded-full min-w-[70px] h-[30px] cursor-pointer border-1 border-gray-200",
                      option.value == rowFilter ? "" : "bg-white text-black",
                      "transition-all duration-150 active:scale-95 ease-out"
                    )}
                    onClick={() => onRowFilterChange(option.value)}
                  >
                    {option.indicator ? <div className={cn("rounded-full h-2 w-2", option.indicator)} /> : <></>} {option.label}
                  </Badge>
                ))}
              </div>
              <Input
                type="text"
                placeholder="Search name, property or message"
                value={globalFilter ?? ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="w-full sm:w-72 bg-white h-[40px]"
                startIcon={<Search className="w-4 h-4" />}
              />
            </div>
            {isRowsLoading ? <Loader /> :
              <DataTable
                data={rowsData?.data || []}
                columns={columns}
                pageNumber={pageNumber}
                pageSize={pageSize}
                setPageNumber={setPageNumber}
                setPageSize={setPageSize}
                totalPages={Math.ceil((rowsData?.total ?? 0) / pageSize)}
                totalRecords={rowsData?.total}
                serverSidePagination={true}
                sorting={sorting}
                onSortingChange={setSorting}
                onRowClick={(row: any) => setSelectedRow(row.original)}
              />
            }
          </div>
        </>
        :
        <div className="bg-white p-4 rounded-md text-[14px] text-gray-600">
          Nothing was imported from this file. Download the <b>Error Log</b> to see the rows that failed validation,
          fix them in the file and upload it again from <b>Bulk Upload</b>.
        </div>
      }

      {selectedRow ?
        <RowDetailDialog row={selectedRow} onClose={() => setSelectedRow(null)} onOpenUser={onOpenUser} /> : <></>}
    </div>
  );
};

export default UploadDetail;
